import { useRef, useEffect, useCallback } from 'react';
import { preloadedUrls } from '@/services/videoPreloadService';

/** Seconds of preview before looping back to 0. */
const PREVIEW_SECONDS = 4;

/**
 * Manages hover-based video preview for thumbnail tiles.
 *
 * Best practices applied:
 * - Lazy src: set only on first hover — nothing is fetched at render time
 * - `#t=0,N` media fragment: hints the browser/CDN to buffer only the first N seconds
 * - 5-second loop via `timeupdate` (not the `loop` attribute, which loops the whole file)
 * - 100 ms enter-debounce: skips quick mouse-passes, saving network round-trips
 * - Shares `preloadedUrls` with videoPreloadService: if the URL was already
 *   buffered in the background, the video.load() call is skipped entirely so
 *   the first hover plays instantly from the HTTP cache.
 * - Unmount cleanup: removes `src` + calls `load()` to abort in-flight requests
 *   and release the video buffer memory
 *
 * When `src` is undefined the hook still manages play/pause for a static fallback
 * src that was set directly on the <video> element (no lazy loading).
 */
export function useHoverVideoPreview(src?: string) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds a cleanup fn that removes a pending loadeddata listener
  const loadedCleanupRef = useRef<(() => void) | null>(null);

  // Loop within the first PREVIEW_SECONDS when a real src is active
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video && video.currentTime >= PREVIEW_SECONDS) {
      video.currentTime = 0;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // 60 ms debounce — long enough to skip a fast mouse-pass, short enough that
    // an intentional hover starts almost immediately. Paired with the CDN
    // preconnect (connectionWarmService), the fetch that follows is already warm.
    hoverTimerRef.current = setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;

      let srcChanged = false;
      if (src) {
        if (!preloadedUrls.has(src)) {
          video.src = `${src}#t=0,${PREVIEW_SECONDS}`;
          preloadedUrls.add(src);
          srcChanged = true;
        } else if (!video.src || !video.src.includes(src)) {
          video.src = `${src}#t=0,${PREVIEW_SECONDS}`;
          srcChanged = true;
        }
      }

      const doPlay = () => {
        loadedCleanupRef.current = null;
        video.currentTime = 0;
        video.play().catch(() => { /* autoplay blocked */ });
      };

      // If src was just changed or the video has no buffered data yet,
      // wait for the first frame before seeking — otherwise currentTime=0
      // is silently ignored while the element is in HAVE_NOTHING state.
      if (srcChanged || video.readyState < 2 /* HAVE_CURRENT_DATA */) {
        const onLoaded = () => doPlay();
        video.addEventListener('loadeddata', onLoaded, { once: true });
        loadedCleanupRef.current = () => video.removeEventListener('loadeddata', onLoaded);
        // Kick off loading — without this, preload="none" videos never fire loadeddata
        video.load();
        return;
      }

      doPlay();
    }, 60);
  }, [src]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // Cancel any pending loadeddata callback so a slow-loading video
    // doesn't start playing after the user has already moved away
    if (loadedCleanupRef.current) {
      loadedCleanupRef.current();
      loadedCleanupRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  // NOTE: previews load on hover ONLY. We deliberately do NOT pre-fetch tiles
  // as they scroll into view — doing so fired a network request for every tile
  // (and repeated calls for the shared demo file) while scrolling the grid. The
  // CDN connection is already warmed via connectionWarmService's preconnect, so
  // the first hover is snappy without any scroll-triggered downloads.

  // Attach timeupdate only when a real preview src is provided
  useEffect(() => {
    if (!src) return;
    const video = videoRef.current;
    if (!video) return;
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [src, handleTimeUpdate]);

  // Release video resources on unmount — prevents memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (loadedCleanupRef.current) loadedCleanupRef.current();
      const video = videoRef.current;
      if (video && src) {
        video.pause();
        video.removeAttribute('src');
        video.load(); // aborts any in-flight network requests + frees buffer
      }
    };
    // Intentionally omitting `src` — we want the refs captured at mount time for cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { videoRef, handleMouseEnter, handleMouseLeave };
}
